import { RefObject, useCallback, useEffect, useRef } from "react";

/**
 * Slides a pinned header up and out of view as the next row after its subtree scrolls into it.
 *
 * The sticky elements are the row's cells, not the row itself. Putting the transform on the row
 * would break the cells' `position: sticky` (a transformed ancestor becomes their containing
 * block), so we transform the cells directly. Returns a ref for the row — its direct `[role]`
 * children are those cells.
 */
const useSpacesListStickyRowEviction = (
  isActiveSticky: boolean,
  evictionAnchor: number | undefined,
  tableRef: RefObject<HTMLDivElement | null>
) => {
  const rowRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const offsetRef = useRef(0);

  const slideCells = useCallback((offset: number) => {
    const value = offset < 0 ? `translateY(${offset}px)` : "";

    for (const cell of rowRef.current?.querySelectorAll<HTMLElement>(":scope > [role]") ?? []) {
      cell.style.transform = value;
    }
  }, []);

  useEffect(() => {
    const cancelPending = () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };

    const tableElement = tableRef.current;

    if (!isActiveSticky || !evictionAnchor || !tableElement) {
      cancelPending();
      slideCells(0);

      return undefined;
    }

    const update = (scrollTop: number) => {
      offsetRef.current = Math.min(0, evictionAnchor - scrollTop);

      if (rafRef.current === null) {
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = null;
          slideCells(offsetRef.current);
        });
      }
    };

    // Run once now so a header that becomes pinned mid-scroll gets its slide right away, instead of
    // showing one frame at its resting slot before the first scroll event arrives.
    update(tableElement.scrollTop);

    const handleScroll = (event: Event) =>
      update((event.currentTarget as HTMLDivElement).scrollTop);

    tableElement.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      tableElement.removeEventListener("scroll", handleScroll);
      cancelPending();
    };
  }, [isActiveSticky, evictionAnchor, tableRef, slideCells]);

  return rowRef;
};

export default useSpacesListStickyRowEviction;
